import { useMemo, useState } from 'react';
import {
  direct,
  isSphereId,
  anchorPhrase,
  methodFor,
  type Observation,
  type SphereId,
} from '@superposition/domain';
import * as api from '../../lib/api.js';
import { useLoadedState } from '../../lib/store.js';
import { haptic } from '../../lib/telegram.js';
import { useSphereName } from '../../lib/names.js';
import { useLakePractices, useLocale, useProfile } from '../../lib/profile.js';
import { Reports } from './Reports.js';
import { useT } from '../../i18n/index.js';

const PAGE = 10;

function newObsId(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  const raw = g.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  return `o${raw.replace(/-/g, '').slice(0, 24)}`;
}

function Dial(): React.ReactNode {
  const lines = useMemo(() => {
    const cx = 52;
    const cy = 52;
    const out: React.ReactNode[] = [];
    for (let i = 0; i < 12; i++) {
      const th = (i * 30 * Math.PI) / 180;
      const red = i === 0 || i === 8;
      const ro = red ? 49 : 46;
      const ri = red ? 34 : 40;
      out.push(
        <line
          key={i}
          x1={(cx + ro * Math.sin(th)).toFixed(1)}
          y1={(cy - ro * Math.cos(th)).toFixed(1)}
          x2={(cx + ri * Math.sin(th)).toFixed(1)}
          y2={(cy - ri * Math.cos(th)).toFixed(1)}
          stroke={red ? '#e01f26' : '#cfcfca'}
          strokeWidth={red ? 2.6 : 1.4}
          strokeLinecap="round"
        />,
      );
    }
    return out;
  }, []);
  return (
    <svg className="dial" viewBox="0 0 104 104" aria-hidden="true">
      {lines}
    </svg>
  );
}

export function ObserverScreen({
  onOpenMethod,
  onOpenLake,
}: {
  /** уводит на экран «метод», на главу под этой провокацией */
  onOpenMethod?: (chapterId: string, anchor?: string) => void;
  /** уводит в озеро с уже выбранной сферой */
  onOpenLake?: (sphere: SphereId) => void;
}): React.ReactNode {
  const { state, store } = useLoadedState();
  const sphereName = useSphereName();
  const t = useT();
  const locale = useLocale();
  const profile = useProfile();
  const practices = useLakePractices();
  const [shown, setShown] = useState(PAGE);

  const latest = state.observations[0] ?? null;

  const [looking, setLooking] = useState(false);

  const look = async (): Promise<void> => {
    if (looking) return;
    haptic('medium');
    // смысл реплики выбирают правила на устройстве: seed фиксирован днём и числом
    // наблюдений, вывод воспроизводим и проверяется тестом
    const seed = `${store.today}#${state.observations.length}`;
    const out = direct({
      values: state.values,
      skills: state.skills,
      history: state.history,
      today: store.today,
      seed,
      lastMechanic: state.observations[0]?.refs?.mech,
      sphereNames: state.sphereNames,
      gender: profile?.gender === 'm' || profile?.gender === 'f' ? profile.gender : null,
      ...(practices ? { lakeSpheres: practices.map((p) => p.sphere).filter(isSphereId) } : {}),
      locale,
    });
    // модель переписывает текст живыми словами; не успела или ошиблась — текст правил
    setLooking(true);
    let words = { lead: out.lead, body: out.body, act: out.act };
    try {
      const r = await api.rephraseObservation(words);
      if (r.rephrased) words = { lead: r.lead, body: r.body, act: r.act };
    } catch {
      // без сети наблюдатель все равно отвечает
    } finally {
      setLooking(false);
    }
    const obs: Observation = { id: newObsId(), date: store.today, ...out, ...words };
    store.update((prev) => ({ ...prev, observations: [obs, ...prev.observations] }));
  };

  return (
    <section className="fade" aria-labelledby="obs-title">
      <h2 className="screen-t" id="obs-title">
        {t.observer.title}
      </h2>

      <div className="obs-hero">
        <button
          type="button"
          className={`obs-eye${looking ? ' looking' : ''}`}
          onClick={() => void look()}
          aria-busy={looking}
          aria-describedby="obs-hint"
        >
          <Dial />
          {looking ? (
            <span>{t.observer.looking}</span>
          ) : (
            <span>
              {t.observer.lookLine1}
              <br />
              {t.observer.lookLine2}
            </span>
          )}
        </button>
        <p className="obs-hint" id="obs-hint">
          {t.observer.hint}
        </p>
      </div>

      {latest ? (
        <div className="obs-card fade">
          {/* всё выводится текстовыми узлами: разметка из импорта не исполняется */}
          {latest.refs?.persona ? (
            <p className="obs-persona">
              {t.observer.persona}{' '}
              {onOpenMethod ? (
                <button
                  type="button"
                  className="obs-link"
                  onClick={() => {
                    haptic('light');
                    const p = latest.refs?.persona;
                    if (p) onOpenMethod(p.chapterId, anchorPhrase(p, locale));
                  }}
                >
                  {latest.refs.persona.name}
                </button>
              ) : (
                latest.refs.persona.name
              )}
            </p>
          ) : null}
          <p className="lead">{latest.lead}</p>
          {latest.body.split('\n\n').map((para, i) => (
            <p className="body" key={i}>
              {para}
            </p>
          ))}
          <div className="obs-act">
            <span className="k">{t.observer.oneStep}</span>
            <span className="v">{latest.act}</span>
          </div>
          {onOpenMethod ? (
            <p className="obs-refs">
              {t.observer.whyInBook}{' '}
              {(latest.refs?.chapters.length ? latest.refs.chapters : [methodFor(latest)]).map((ch, i, all) => (
                <span key={ch.chapterId}>
                  <button
                    type="button"
                    className="obs-link"
                    onClick={() => {
                      haptic('light');
                      onOpenMethod(ch.chapterId, anchorPhrase(ch, locale));
                    }}
                  >
                    {ch.hint}
                  </button>
                  {i < all.length - 1 ? ', ' : ''}
                </span>
              ))}
            </p>
          ) : null}
          {onOpenLake && latest.refs?.lake ? (
            <button
              type="button"
              className="obs-method"
              onClick={() => {
                haptic('light');
                onOpenLake(latest.refs?.lake as SphereId);
              }}
            >
              {t.observer.lakePractice({ sphere: sphereName(latest.refs.lake) })}
            </button>
          ) : null}
        </div>
      ) : (
        <p className="obs-empty">
          {t.observer.emptyLine1}
          <br />
          {t.observer.emptyLine2}
        </p>
      )}

      {state.observations.length > 1 ? (
        <div className="obs-log">
          <h4>{t.observer.past({ count: state.observations.length - 1 })}</h4>
          <ul>
            {state.observations.slice(1, shown).map((o) => (
              <li key={o.id}>
                <span className="d">{t.common.formatDay(o.date)}</span>
                {o.lead}
              </li>
            ))}
          </ul>
          {state.observations.length - 1 > shown - 1 ? (
            <button type="button" className="more-btn" onClick={() => setShown((s) => s + PAGE)}>
              {t.observer.showMore}
            </button>
          ) : null}
        </div>
      ) : null}

      <Reports />

      <p className="lake-note" style={{ marginTop: 18 }}>
        {t.observer.note}
      </p>
    </section>
  );
}
